import * as React from 'react';
import styled, { css } from 'styled-components';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { JRCIconButton } from '../JRCIconButton/JRCIconButton';
import { JRCConditionalWrapper } from '../Common/JRCConditionalWrapper';
import { useIntl } from 'react-intl';
import { DropTargetMonitor } from 'react-dnd';
import { JRCDraggingPlaceholder } from '../Common/JRCDraggingPlaceholder';

const TableCss = css`
    border-bottom: 1px solid ${(props) => props.theme.color.grey2};
    text-align: left;
    vertical-align: middle;
    align-content: center;
`;

export const Table = styled.table<{ loading?: boolean }>`
    ${TableCss};
    width: 100%;
    border-collapse: collapse;

    ${({ loading }) => loading && 'border-bottom: none;'}
`;

export const Thead = styled.thead`
    position: sticky;
    top: 0;
    background-color: white;
    border-bottom: 2px solid ${(props) => props.theme.color.grey2};
    z-index: 1;
`;

export const TBody = styled.tbody``;

export const Th = styled.th<{ width?: number | string }>`
    ${TableCss};
    font-size: 12px;
    padding: 0 4px;
    font-weight: normal;
    ${(props) => props.width && props.width > 0 && `width: ${props.width}px`};
`;

export const Tr = styled.tr<{ dragging?: boolean }>`
    height: 32px;
    ${(props) => props.dragging && JRCDraggingPlaceholder};
`;

export const Td = styled.td`
    ${TableCss};
    padding: 0 4px;
    ${(props) => props.width && `width: ${props.width}`};
`;

const DraggableTd = styled.td`
    ${TableCss};
    display: table-cell;
    width: 56px;
    ${(props) => props.width && props.width > 0 && `width: ${props.width}px`};
`;

export const LoadMore = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    flex: 1 100%;
    margin-top: 16px;
`;

export type DraggableTBodyProps = {
    draggable: boolean;
    children: Array<JSX.Element>;
};

export type DraggableTrProps = {
    index: number;
    onDrag: (dragIndex: number, dropIndex: number) => void;
    onDrop: () => void;
    draggable: boolean;
    children: Array<JSX.Element>;
};

export const DraggableTBody = ({ draggable, children }: DraggableTBodyProps) => {
    return (
        <JRCConditionalWrapper
            condition={draggable}
            wrapper={(children) => <DndProvider backend={HTML5Backend}>{children}</DndProvider>}>
            <>{children}</>
        </JRCConditionalWrapper>
    );
};

const DND_ITEM_TYPE = 'row';

export const DraggableTr = ({ index, onDrag, onDrop, draggable, children }: DraggableTrProps) => {
    const intl = useIntl();

    const dropRef = React.useRef<HTMLTableRowElement>(null);
    const dragRef = React.useRef<HTMLTableCellElement>(null);
    let dragging = false;

    if (draggable) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [, drop] = useDrop({
            accept: DND_ITEM_TYPE,
            hover(item: any, monitor: DropTargetMonitor) {
                if (!dropRef.current) {
                    return;
                }

                const dragIndex = item.index;
                const hoverIndex = index;

                if (dragIndex === hoverIndex) {
                    return;
                }

                const hoverBoundingRect = dropRef.current.getBoundingClientRect();
                const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
                const clientOffset = monitor.getClientOffset() || { y: 1000 };
                const hoverClientY = clientOffset.y - hoverBoundingRect.top;

                if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                    return;
                }

                if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                    return;
                }

                onDrag(dragIndex, hoverIndex);

                item.index = hoverIndex;
            },
        });

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [{ isDragging }, drag, preview] = useDrag({
            type: DND_ITEM_TYPE,
            item: { type: DND_ITEM_TYPE, index },
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
            end() {
                onDrop();
            },
        });

        dragging = isDragging;

        preview(drop(dropRef));
        drag(dragRef);
    }

    return (
        <JRCConditionalWrapper
            condition={draggable}
            wrapper={(children) => (
                <Tr ref={dropRef} dragging={dragging}>
                    <DraggableTd ref={dragRef}>
                        <JRCIconButton
                            icon="icon-fs-dw"
                            tooltip={{ description: intl.formatMessage({ id: 'GLOBAL_Move' }), position: 'right' }}
                        />
                        &nbsp;
                    </DraggableTd>
                    {children}
                </Tr>
            )}
            wrapperFalse={(children) => <Tr>{children}</Tr>}>
            <React.Fragment>{children}</React.Fragment>
        </JRCConditionalWrapper>
    );
};
